import { getClient } from "@/lib/sanity/server";
import { groq } from "next-sanity";
import { useEffect } from "react";
import useSWR from "swr";
import { z } from "zod";
import { useRouter } from "next/router";
import ProductCardComponent from "@/components/ProductCard";
import { Container, Heading, ProductsWrapper } from "./styles";

// Cast to fix React 17 + @types/react 18 compatibility issue
const ProductCard = ProductCardComponent as any;

const PAGE_SIZE = 20;

const featuredProductsBodyQuery = groq`
[$start...$end] {
  _id,
  title,
  "slug": slug.current,
  thumbnail {
    asset-> {
      url,
      metadata,
      "alt": coalesce(alt[$locale], alt[$defaultLocale])
    }
  },
  "param1": parameters[attribute._ref == "product-type"][0] {
    "name": attribute->.name,
    values,
  },
  "param2": parameters[attribute._ref == "route-of-administration"][0] {
    "name": attribute->.name,
    values,
  },
  "param3": parameters[attribute._ref == "material"][0] {
    "name": attribute->.name,
    values,
  },
  "eco": parameters[attribute._ref == "ecopositive"][0].values[0],
}`;

const featuredProductsQueryDataSchema = z
  .array(
    z.object({
      _id: z.string(),
      title: z.string(),
      slug: z.string(),
      thumbnail: z
        .object({
          asset: z.object({
            alt: z.string().nullable(),
            metadata: z
              .object({
                dimensions: z.object({
                  width: z.number(),
                  height: z.number(),
                }),
              })
              .nullable(),
            url: z.string(),
          }),
        })
        .nullable(),
      param1: z.object({
        name: z.string(),
        values: z.array(z.string()),
      }),
      param2: z.object({
        name: z.string(),
        values: z.array(z.string()),
      }),
      param3: z.object({
        name: z.string(),
        values: z.array(z.string()),
      }),
      eco: z.string().nullable(),
    })
  )
  .nullable();

type FeaturedProductsData = z.infer<typeof featuredProductsQueryDataSchema>;

const fetcher = async (
  query: string,
  params: {
    start: number;
    end: number;
    locale: string;
    defaultLocale: string;
  }
) => {
  const res = await getClient().fetch(query, params);
  return featuredProductsQueryDataSchema.parse(res);
};

type Props = {
  rules: [
    {
      key: string;
      value: string;
    }
  ];
  data: any[];
  title: string;
};

const FeaturedProducts: React.FC<Props> = (props) => {
  const { locale = "it", defaultLocale = "it" } = useRouter();
  let params = {
    start: 0,
    end: PAGE_SIZE,
    locale,
    defaultLocale,
  };
  const { rules, title } = props;
  let selectQuery = "*[_type == 'product'";
  const selectAttributeQuery =
    "'$value' in parameters[@.attribute._key == '$key'].values[]";

  rules.forEach((rule) => {
    if (rule.key !== "" && rule.value !== "") {
      let attributeAndQuery = selectAttributeQuery
        .replace("$value", rule.value)
        .replace("$key", rule.key);
      if (rule.value.indexOf("|") !== -1) {
        const ruleValues = rule.value.split("|").map((value) => value.trim());
        attributeAndQuery = "(";
        ruleValues.forEach((ruleValue, index) => {
          attributeAndQuery += selectAttributeQuery
            .replace("$value", ruleValue)
            .replace("$key", rule.key);
          if (index < ruleValues.length - 1) attributeAndQuery += " || ";
        });
        attributeAndQuery += ")";
      }
      selectQuery += ` && ${attributeAndQuery}`;
    }
  });
  selectQuery += `]${featuredProductsBodyQuery}`;

  const { data, error } = useSWR([selectQuery, params], fetcher);

  useEffect(() => {
    if (error) console.error(error);
  }, [error]);

  return (
    <Container>
      {data && data.length > 0 && title && <Heading as="h3">{title}</Heading>}
      <ProductsWrapper>
        {data?.map((product) => {
          const image = {
            asset: {
              url: product?.thumbnail?.asset.url,
              alt: product?.thumbnail?.asset.alt,
            },
          };
          const param1 = {
            label: product.param1.name,
            value: product.param1.values?.join(", ") || "",
          };
          const param2 = {
            label: product.param2.name,
            value: product.param2.values?.join(", ") || "",
          };
          const param3 = {
            label: product.param3.name,
            value: product.param3.values?.join(", ") || "",
          };
          const eco = product.eco ? product.eco[0] : "";

          return (
            <ProductCard
              key={product._id}
              image={image}
              slug={product.slug}
              name={product.title}
              subheading={param1.value}
              param1={param2}
              param2={param3}
              eco={eco}
            />
          );
        })}
      </ProductsWrapper>
    </Container>
  );
};

export default FeaturedProducts;
